import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Badge from "antd/es/badge";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import { Avatar, EntityTypeShapedIcon } from "@mapmycustomers/ui";

import ItemComponentProps from "@app/component/DnDBoard/types/ItemComponentProps";
import UserPreviewCard from "@app/component/UserPreviewCard";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import DealInfo from "@app/scene/deal/component/DealInfo";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import { showEntityView } from "@app/store/entityView/actions";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import styles from "./DealCard.module.scss";

interface Props extends ItemComponentProps<Deal> {
  showEntityView: typeof showEntityView;
}

const DealCard: React.FC<Props> = ({ item: deal, showEntityView }) => {
  const intl = useIntl();
  const history = useHistory();
  const { id, account, amount, closingDate, contact, name, stage, user } = deal;

  const handleDealClick = useCallback(() => {
    history.push(`${Path.DEAL}/${ViewMode.FORECAST}/${id}`);
  }, [id, history]);

  const handleCompanyClick = useCallback(() => {
    if (account) {
      showEntityView({ entityId: account.id, entityType: EntityType.COMPANY });
    }
  }, [account, showEntityView]);

  const handlePersonClick = useCallback(() => {
    if (contact) {
      showEntityView({ entityId: contact.id, entityType: EntityType.PERSON });
    }
  }, [contact, showEntityView]);

  const location = useMemo(() => {
    if (account?.city) {
      return `${account.city}${account?.region ? `, ${account.region}` : ""}`;
    }
    if (contact?.city) {
      return `${contact.city}${contact?.region ? `, ${contact.region}` : ""}`;
    }
    return intl.formatMessage({
      id: "dealCards.card.noLocation",
      defaultMessage: "No Location",
      description: "Deal card no location text",
    });
  }, [intl, account, contact]);

  const borderLeft = useMemo(() => `0.25rem solid ${anyColorToHex(stage.color)}`, [stage]);

  const parsedClosingDate = closingDate ? parseApiDateWithUtcTz(closingDate) : undefined;

  return (
    <div className={styles.container} onClick={handleDealClick} style={{ borderLeft }}>
      <div className={styles.title}>
        <Tooltip
          placement="top"
          title={intl.formatMessage({
            id: "dealCards.card.name.tooltip",
            defaultMessage: "Edit deal",
            description: "Deal card Name tooltip",
          })}
        >
          {name}
        </Tooltip>
      </div>
      <div className={styles.subRow}>
        <div className={styles.amount}>
          {amount
            ? intl.formatNumber(amount, {
                currency: localSettings.getCurrencyCode(),
                minimumFractionDigits: 0,
                style: "currency",
              })
            : intl.formatMessage({
                id: "dealCards.card.noAmount",
                defaultMessage: "No Amount",
                description: "Deal card no amount text",
              })}
        </div>
        <div className={styles.location}>{location}</div>
        <Tooltip
          placement="bottom"
          title={
            <Row>
              <Col>
                <Badge color={anyColorToHex(deal.stage.color)} />
              </Col>
              <Col>
                <div className={styles.stageName}>{deal.stage.name}</div>
                <div className={styles.funnelName}>{deal.funnel.name}</div>
              </Col>
            </Row>
          }
        >
          <svg height="0.875rem" viewBox="0 0 615 512" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 92.16C0 41.261 41.261 0 92.16 0c50.899 0 92.16 41.261 92.16 92.16v307.2c0 50.9-41.261 92.16-92.16 92.16C41.261 491.52 0 450.26 0 399.36V92.16zm245.76 61.441c0-50.899 41.26-92.16 92.16-92.16 50.896 0 92.16 41.261 92.16 92.16v184.32c0 50.899-41.264 92.159-92.16 92.159-50.9 0-92.16-41.26-92.16-92.16V153.602zm245.76 30.719c0-33.933 27.508-61.44 61.44-61.44s61.44 27.507 61.44 61.44V307.2c0 33.932-27.508 61.44-61.44 61.44s-61.44-27.508-61.44-61.44V184.32z"
              fill={anyColorToHex(deal.stage.color)}
            />
          </svg>
        </Tooltip>
      </div>
      <div className={styles.closingDate}>
        <Tag color="default">
          {isValidDate(parsedClosingDate) ? (
            <Tooltip
              placement="top"
              title={intl.formatMessage({
                id: "dealCards.card.closingDate.tooltip",
                defaultMessage: "Expected Close",
                description: "Deal card Expected Close tooltip",
              })}
            >
              {formatDate(parsedClosingDate, "P")}
            </Tooltip>
          ) : (
            intl.formatMessage({
              id: "dealCards.card.noCloseDate",
              defaultMessage: "No Closing Date",
              description: "Deal card no closing date text",
            })
          )}
        </Tag>
      </div>
      <div className={styles.bottom}>
        <div className={styles.icons}>
          <Tooltip title={user.fullName}>
            <UserPreviewCard userId={user.id}>
              <Avatar actionOnHover size="small" tooltip={false} user={user} />
            </UserPreviewCard>
          </Tooltip>
          <div className={styles.relations}>
            {account && (
              <Tooltip title={account.name}>
                <EntityTypeShapedIcon
                  className={styles.relationIconContainer}
                  entityType={EntityType.COMPANY}
                  iconClassName={styles.associationIcon}
                  onClick={handleCompanyClick}
                  size="xs"
                />
              </Tooltip>
            )}
            {contact && (
              <Tooltip title={contact.name}>
                <EntityTypeShapedIcon
                  className={styles.relationIconContainer}
                  entityType={EntityType.PERSON}
                  iconClassName={styles.associationIcon}
                  onClick={handlePersonClick}
                  size="xs"
                />
              </Tooltip>
            )}
          </div>
        </div>
        <DealInfo deal={deal} />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(DealCard);
